import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Users, CheckSquare, MessageSquare, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import type { Task, TaskStatus, TaskPriority, BoardGrouping } from '@/types/task';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TasksBoardViewProps {
  readonly tasks: Task[];
  readonly loading?: boolean;
  readonly onTaskClick: (task: Task) => void;
  readonly onTaskUpdate: (task: Task) => void;
  readonly groupBy?: BoardGrouping;
}

export function TasksBoardView({ tasks, loading, groupBy = 'status' }: Readonly<TasksBoardViewProps>) {
  const navigate = useNavigate();
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(new Set());

  const statusLanes: { id: TaskStatus; title: string }[] = [
    { id: 'New', title: 'New' },
    { id: 'In Progress', title: 'In Progress' },
    { id: 'Blocked', title: 'Blocked' },
    { id: 'Resolved', title: 'Resolved' },
    { id: 'Closed', title: 'Closed' },
  ];

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'High': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'Low': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'P0': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'P1': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'P2': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'P3': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'Bug': return 'text-red-600';
      case 'Feature': return 'text-blue-600';
      case 'Enhancement': return 'text-purple-600';
      case 'Task': return 'text-green-600';
      case 'Documentation': return 'text-amber-600';
      case 'Research': return 'text-indigo-600';
      case 'Support': return 'text-cyan-600';
      default: return 'text-gray-600';
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const isOverdue = date < today;
    const isToday = date.toDateString() === today.toDateString();
    
    let label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (isToday) label = 'Today';
    
    return { label, isOverdue, isToday };
  };

  const getChecklistProgress = (task: Task) => {
    if (task.checklists.length === 0) return null;
    const completed = task.checklists.filter(c => c.completed).length;
    const total = task.checklists.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const toggleLaneCollapse = (laneId: string) => {
    const newCollapsed = new Set(collapsedLanes);
    if (newCollapsed.has(laneId)) {
      newCollapsed.delete(laneId);
    } else {
      newCollapsed.add(laneId);
    }
    setCollapsedLanes(newCollapsed);
  };

  const groupTasks = () => {
    if (!tasks) return [];
    
    if (groupBy === 'status') {
      return statusLanes.map(lane => ({
        ...lane,
        tasks: tasks.filter(t => t.status === lane.id),
      }));
    }
    
    if (groupBy === 'assignee') {
      // Group by assignee
      const assigneeMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        const assigneeId = task.assignee?.id || 'unassigned';
        if (!assigneeMap.has(assigneeId)) {
          assigneeMap.set(assigneeId, []);
        }
        assigneeMap.get(assigneeId)!.push(task);
      });
      
      return Array.from(assigneeMap.entries()).map(([id, tasks]) => ({
        id,
        title: tasks[0]?.assignee?.name || 'Unassigned',
        tasks,
      }));
    }
    
    if (groupBy === 'priority') {
      const priorities: TaskPriority[] = ['Urgent', 'High', 'Medium', 'Low'];
      return priorities.map(priority => ({
        id: priority,
        title: priority,
        tasks: tasks.filter(t => t.priority === priority),
      }));
    }
    
    if (groupBy === 'type') {
      // Group by type
      const typeMap = new Map<string, Task[]>();
      tasks.forEach(task => {
        const type = task.type || 'Other';
        if (!typeMap.has(type)) {
          typeMap.set(type, []);
        }
        typeMap.get(type)!.push(task);
      });
      
      return Array.from(typeMap.entries()).map(([id, tasks]) => ({
        id,
        title: id,
        tasks,
      }));
    }
    
    return [];
  };

  const lanes = groupTasks();

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="flex gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={`lane-skeleton-${i}`} className="flex-1 min-w-[300px]">
              <Skeleton className="h-12 mb-4" />
              <div className="space-y-3">
                {[...Array(3)].map((_, j) => (
                  <Skeleton key={`card-skeleton-${j}`} className="h-32" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pb-6">
      {/* Board Lanes - Full page scroll */}
      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max items-start">
          {lanes.map((lane) => {
            const isCollapsed = collapsedLanes.has(lane.id);
            
            return (
              <div
                key={lane.id}
                className={cn(
                  'flex flex-col shrink-0 transition-all',
                  isCollapsed ? 'w-16' : 'w-[350px]'
                )}
              >
                {/* Lane Header */}
                <div className="p-3 shrink-0 border-b">
                  <div className="flex items-center justify-between">
                    {!isCollapsed && (
                      <>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm text-muted-foreground">{lane.title}</h3>
                          <Badge variant="secondary" className="rounded-full">
                            {lane.tasks.length}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleLaneCollapse(lane.id)}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                    {isCollapsed && (
                      <div className="flex flex-col items-center gap-2 w-full">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => toggleLaneCollapse(lane.id)}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <div className="writing-mode-vertical text-xs font-medium text-muted-foreground whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                          {lane.title}
                        </div>
                        <Badge variant="secondary" className="rounded-full text-xs">
                          {lane.tasks.length}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lane Content - No individual scroll */}
                {!isCollapsed && (
                  <div className="p-3 bg-muted/10">
                    <div className="space-y-3">
                      {lane.tasks.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          No tasks
                        </div>
                      ) : (
                        lane.tasks.map((task) => {
                          const dueDate = formatDueDate(task.dueDate);
                          const progress = getChecklistProgress(task);

                          return (
                            <Card
                              key={task.id}
                              className="cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => navigate(`/tasks/${task.id}`)}
                            >
                              <CardContent className="p-4 space-y-3">
                                {/* Type & Priority */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {task.type && (
                                      <span className={cn('text-xs font-medium', getTypeColor(task.type))}>
                                        {task.type}
                                      </span>
                                    )}
                                    <Badge className={cn('text-xs px-2 py-0.5', getPriorityBadgeColor(task.priority))}>
                                      {task.priority}
                                    </Badge>
                                  </div>
                                  {dueDate && (
                                    <div className="flex items-center gap-1.5">
                                      <Calendar className={cn(
                                        'h-3 w-3',
                                        dueDate.isOverdue && 'text-red-600',
                                        !dueDate.isOverdue && dueDate.isToday && 'text-orange-600',
                                        !dueDate.isOverdue && !dueDate.isToday && 'text-muted-foreground'
                                      )} />
                                      <span className={cn(
                                        'text-xs',
                                        dueDate.isOverdue && 'text-red-600 font-medium',
                                        !dueDate.isOverdue && dueDate.isToday && 'text-orange-600 font-medium',
                                        !dueDate.isOverdue && !dueDate.isToday && 'text-muted-foreground'
                                      )}>
                                        {dueDate.label}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Title */}
                                <div>
                                  <div className="font-medium text-sm line-clamp-2 text-primary">{task.title}</div>
                                  <div className="text-xs text-muted-foreground mt-1">{task.id}</div>
                                </div>

                                {/* Assignee */}
                                {task.assignee && (
                                  <div className="flex items-center gap-2">
                                    {task.assignee.type === 'user' ? (
                                      <Avatar className="h-6 w-6">
                                        <AvatarImage src={task.assignee.avatar} />
                                        <AvatarFallback className="text-xs">
                                          {task.assignee.name.split(' ').map(n => n[0]).join('')}
                                        </AvatarFallback>
                                      </Avatar>
                                    ) : (
                                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Users className="h-3 w-3 text-primary" />
                                      </div>
                                    )}
                                    <span className="text-xs truncate">{task.assignee.name}</span>
                                  </div>
                                )}

                                {/* Progress & Metadata */}
                                <div className="flex items-center justify-between pt-2 border-t">
                                  {progress ? (
                                    <div className="flex items-center gap-2 flex-1">
                                      <CheckSquare className="h-3 w-3 text-muted-foreground" />
                                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                          className={cn('h-full transition-all', getProgressColor(progress.percentage))}
                                          style={{ width: `${progress.percentage}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-muted-foreground">
                                        {progress.completed}/{progress.total}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex-1" />
                                  )}
                                  
                                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                    {task.commentsCount > 0 && (
                                      <span className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3" />
                                        {task.commentsCount}
                                      </span>
                                    )}
                                    {task.attachmentsCount > 0 && (
                                      <span className="flex items-center gap-1">
                                        <Paperclip className="h-3 w-3" />
                                        {task.attachmentsCount}
                                      </span>
                                    )}
                                    {task.subtasks.length > 0 && (
                                      <span className="flex items-center gap-1">
                                        <CheckSquare className="h-3 w-3" />
                                        {task.subtasks.filter(s => s.status === 'Resolved').length}/{task.subtasks.length}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
